// Leaflet map for the New Season editors: block polygons with name labels, the
// selected block highlighted, optional polygon / radial drawing (leaflet.pm),
// optional vertex editing of one block, sensor pins, home / + / − controls,
// "Search by name or coordinates" + Navigate, and a layers toggle.
//
// Built separately from CustomMap (which is left untouched) because CustomMap is
// a dashboard map bound to the V1 field model.
const React = require('react');
const { useEffect, useRef, useState, useCallback } = require('react');
const { useTranslation } = require('react-i18next');
const L = require('leaflet');
require('leaflet.pm');
require('leaflet.pm/dist/leaflet.pm.css');
require('leaflet/dist/leaflet.css');
const { createGoogleTileLayer } = require('../../utils/googleMapTiles');
const { circleToRing, parseCoordinates } = require('./layoutHelpers');
const { DRAW_MODES } = require('./newSeasonConstants');

const DEFAULT_CENTER = [32.095998, 34.776344];
const DEFAULT_ZOOM = 15;
const NAVIGATE_ZOOM = 17;
const FIT_PADDING = [32, 32];
const FOCUS_MAX_ZOOM = 18;

const ringToLatLngs = (ring) =>
  (ring || []).map((point) => [point.latitude, point.longitude]);

const latLngsToRing = (latLngs) =>
  latLngs.map((latLng) => ({ latitude: latLng.lat, longitude: latLng.lng }));

const polygonClassName = (isSelected) =>
  'ns-poly' + (isSelected ? ' ns-poly-selected' : '');

const drawnLayerToAreas = (shape, layer) => {
  if (shape === 'Circle') {
    return [circleToRing(layer.getLatLng(), layer.getRadius())];
  }
  const rings = layer.getLatLngs();
  return [latLngsToRing(Array.isArray(rings[0]) ? rings[0] : rings)];
};

const createMap = (element) => {
  const satellite = createGoogleTileLayer('hybrid');
  const streets = createGoogleTileLayer('roadmap');
  const map = L.map(element, {
    center: DEFAULT_CENTER,
    zoom: DEFAULT_ZOOM,
    zoomControl: false,
    layers: [satellite],
    attributionControl: true,
    fadeAnimation: false,
  });
  map.attributionControl.setPrefix('');
  return { map, baseLayers: { satellite, streets } };
};

const addBlockLayers = (group, block, options) => {
  const { isSelected, isEditable, onClick, onEdited } = options;
  (block.areas || []).forEach((ring, index) => {
    const polygon = L.polygon(ringToLatLngs(ring), {
      className: polygonClassName(isSelected),
    });
    if (index === 0 && block.name) {
      polygon.bindTooltip(block.name, {
        permanent: true,
        direction: 'center',
        className:
          'ns-map-label' + (isSelected ? ' ns-map-label-selected' : ''),
      });
    }
    polygon.on('click', (event) => onClick(block.key, event.originalEvent));
    if (isEditable && block.areas.length === 1) {
      polygon.on('pm:edit', () =>
        onEdited(block.key, [latLngsToRing(polygon.getLatLngs()[0])]),
      );
      polygon.pm.enable({ snappable: false });
    }
    group.addLayer(polygon);
  });
};

const addSensorMarkers = (group, markers) => {
  (markers || []).forEach((marker) => {
    const icon = L.divIcon({
      className:
        'ns-sensor-pin' + (marker.selected ? ' ns-sensor-pin-selected' : ''),
      iconSize: [14, 14],
    });
    const layer = L.marker([marker.lat, marker.lng], {
      icon,
      title: marker.title,
    });
    if (marker.onClick) {
      layer.on('click', marker.onClick);
    }
    group.addLayer(layer);
  });
};

const findBlockByName = (blocks, query) => {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return null;
  }
  return (
    blocks.find((block) => block.name.toLowerCase() === needle) ||
    blocks.find((block) => block.name.toLowerCase().includes(needle)) ||
    null
  );
};

const blockBounds = (block) =>
  L.latLngBounds(
    (block.areas || []).reduce(
      (points, ring) => points.concat(ringToLatLngs(ring)),
      [],
    ),
  );

const MapButton = ({ title, onClick, children, disabled, active }) => (
  <button
    type='button'
    className={'ns-map-btn' + (active ? ' ns-map-btn-active' : '')}
    title={title}
    aria-label={title}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </button>
);

const DrawTools = ({ drawMode, onDrawModeChange }) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const choose = (mode) => {
    setIsMenuOpen(false);
    onDrawModeChange(drawMode === mode ? null : mode);
  };
  return (
    <div className='ns-map-tools'>
      <MapButton
        title={t('new_season_draw')}
        active={Boolean(drawMode) || isMenuOpen}
        onClick={() => setIsMenuOpen((open) => !open)}
      >
        ✎
      </MapButton>
      {isMenuOpen && (
        <div className='ns-map-menu'>
          <button type='button' onClick={() => choose(DRAW_MODES.RADIAL)}>
            {t('new_season_draw_radial')}
          </button>
          <button type='button' onClick={() => choose(DRAW_MODES.FREE_FORM)}>
            {t('new_season_draw_free_form')}
          </button>
          <button type='button' disabled>
            {t('new_season_draw_placeholder_block')}
          </button>
          <button type='button' disabled>
            {t('new_season_draw_landmark')}
          </button>
        </div>
      )}
      <MapButton title={t('new_season_magic_wand')} disabled>
        ✦
      </MapButton>
    </div>
  );
};

const MapSearch = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [notFound, setNotFound] = useState(false);
  const submit = (event) => {
    event.preventDefault();
    setNotFound(!onNavigate(query));
  };
  return (
    <form className='ns-map-search' onSubmit={submit}>
      <input
        className='form-control ns-input'
        value={query}
        placeholder={t('new_season_search_map')}
        onChange={(event) => {
          setQuery(event.target.value);
          setNotFound(false);
        }}
      />
      <button type='submit' className='ns-btn ns-btn-primary ns-btn-small'>
        {t('new_season_navigate')}
      </button>
      {notFound && (
        <div className='ns-map-search-error'>
          {t('new_season_not_found', { query })}
        </div>
      )}
    </form>
  );
};

const useLatest = (value) => {
  const ref = useRef(value);
  ref.current = value;
  return ref;
};

const hasSize = (map) => {
  const size = map.getSize();
  return size.x > 0 && size.y > 0;
};

// Creates the map once. A ResizeObserver keeps Leaflet's size in sync with the
// container: a map measured at 0×0 (laid out after creation) would otherwise
// fit to minimum zoom and clip its vector layers, leaving only the labels.
const useLeafletMap = (containerRef, onDrawnRef, onResizeRef) => {
  const stateRef = useRef(null);
  useEffect(() => {
    const { map, baseLayers } = createMap(containerRef.current);
    const blocksGroup = L.featureGroup().addTo(map);
    const markersGroup = L.featureGroup().addTo(map);
    map.on('pm:create', (event) => {
      const areas = drawnLayerToAreas(event.shape, event.layer);
      map.removeLayer(event.layer);
      onDrawnRef.current?.(areas);
    });
    stateRef.current = {
      map,
      baseLayers,
      blocksGroup,
      markersGroup,
      fitted: false,
    };
    const onResize = () => {
      map.invalidateSize({ pan: false });
      onResizeRef.current?.();
    };
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(onResize)
        : null;
    observer?.observe(containerRef.current);
    const resizeTimer = setTimeout(onResize, 0);
    return () => {
      clearTimeout(resizeTimer);
      observer?.disconnect();
      map.remove();
      stateRef.current = null;
    };
  }, [containerRef, onDrawnRef, onResizeRef]);
  return stateRef;
};

const boundsOfBlocks = (blocks, keys) => {
  const wanted = new Set(keys);
  const bounds = L.latLngBounds([]);
  (blocks || [])
    .filter((block) => wanted.has(block.key))
    .forEach((block) => bounds.extend(blockBounds(block)));
  return bounds;
};

const BlocksMap = ({
  blocks,
  selectedKeys,
  onBlockClick,
  drawMode,
  onDrawModeChange,
  onDrawn,
  editableKey,
  onAreasEdited,
  markers,
  showDrawTools,
  className,
  focusKeys,
  fitKey,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const onDrawnRef = useLatest(onDrawn);
  const onBlockClickRef = useLatest(onBlockClick);
  const onAreasEditedRef = useLatest(onAreasEdited);
  const blocksRef = useLatest(blocks);
  const focusKey = (focusKeys || []).join('|');
  const focusKeyRef = useLatest(focusKey);
  const onResizeRef = useRef(null);
  const leafletRef = useLeafletMap(containerRef, onDrawnRef, onResizeRef);
  const [baseLayer, setBaseLayer] = useState('satellite');

  const fitAll = useCallback(() => {
    const leaflet = leafletRef.current;
    const bounds = leaflet?.blocksGroup.getBounds();
    if (bounds?.isValid() && hasSize(leaflet.map)) {
      leaflet.map.fitBounds(bounds, { padding: FIT_PADDING });
      return true;
    }
    return false;
  }, [leafletRef]);

  // Focused blocks (Season editor: the selected block) if any, else all blocks.
  const fitView = useCallback(() => {
    const leaflet = leafletRef.current;
    const keys = focusKeyRef.current ? focusKeyRef.current.split('|') : [];
    const bounds = keys.length ? boundsOfBlocks(blocksRef.current, keys) : null;
    if (leaflet && bounds?.isValid() && hasSize(leaflet.map)) {
      leaflet.map.fitBounds(bounds, {
        padding: FIT_PADDING,
        maxZoom: FOCUS_MAX_ZOOM,
      });
      return true;
    }
    return fitAll();
  }, [leafletRef, focusKeyRef, blocksRef, fitAll]);

  onResizeRef.current = () => {
    const leaflet = leafletRef.current;
    if (leaflet && !leaflet.fitted) {
      leaflet.fitted = fitView();
    }
  };

  useEffect(() => {
    const leaflet = leafletRef.current;
    if (!leaflet) {
      return;
    }
    const selected = new Set(selectedKeys || []);
    leaflet.blocksGroup.clearLayers();
    (blocks || []).forEach((block) =>
      addBlockLayers(leaflet.blocksGroup, block, {
        isSelected: selected.has(block.key),
        isEditable: block.key === editableKey,
        onClick: (key, event) => onBlockClickRef.current?.(key, event),
        onEdited: (key, areas) => onAreasEditedRef.current?.(key, areas),
      }),
    );
    if (!leaflet.fitted) {
      leaflet.fitted = fitView();
    }
  }, [
    blocks,
    selectedKeys,
    editableKey,
    leafletRef,
    fitView,
    onBlockClickRef,
    onAreasEditedRef,
  ]);

  // Re-fit when the farming unit (fitKey) or the focused blocks change.
  useEffect(() => {
    const leaflet = leafletRef.current;
    if (leaflet) {
      leaflet.fitted = fitView();
    }
  }, [focusKey, fitKey, leafletRef, fitView]);

  useEffect(() => {
    const leaflet = leafletRef.current;
    if (!leaflet) {
      return;
    }
    leaflet.markersGroup.clearLayers();
    addSensorMarkers(leaflet.markersGroup, markers);
  }, [markers, leafletRef]);

  useEffect(() => {
    const map = leafletRef.current?.map;
    if (!map) {
      return undefined;
    }
    map.pm.disableDraw('Poly');
    map.pm.disableDraw('Circle');
    if (drawMode === DRAW_MODES.FREE_FORM) {
      map.pm.enableDraw('Poly', { snappable: true, snapDistance: 10 });
    } else if (drawMode === DRAW_MODES.RADIAL) {
      map.pm.enableDraw('Circle', {});
    }
    const endDraw = () => onDrawModeChange?.(null);
    map.on('pm:create', endDraw);
    return () => map.off('pm:create', endDraw);
  }, [drawMode, leafletRef, onDrawModeChange]);

  useEffect(() => {
    const leaflet = leafletRef.current;
    if (!leaflet) {
      return;
    }
    const { map, baseLayers } = leaflet;
    Object.keys(baseLayers).forEach((name) => {
      if (name !== baseLayer && map.hasLayer(baseLayers[name])) {
        map.removeLayer(baseLayers[name]);
      }
    });
    if (!map.hasLayer(baseLayers[baseLayer])) {
      baseLayers[baseLayer].addTo(map);
    }
  }, [baseLayer, leafletRef]);

  const navigateTo = useCallback(
    (query) => {
      const map = leafletRef.current?.map;
      const coordinates = parseCoordinates(query);
      if (map && coordinates) {
        map.setView([coordinates.lat, coordinates.lng], NAVIGATE_ZOOM);
        return true;
      }
      const block = findBlockByName(blocksRef.current || [], query);
      const bounds = block && blockBounds(block);
      if (map && bounds?.isValid()) {
        map.fitBounds(bounds, { padding: FIT_PADDING });
        return true;
      }
      return false;
    },
    [leafletRef, blocksRef],
  );

  const zoom = (delta) =>
    leafletRef.current?.map.setZoom(leafletRef.current.map.getZoom() + delta);

  return (
    <div className={'ns-map-wrapper ' + (className || '')}>
      <div ref={containerRef} className='ns-map' />
      {showDrawTools && (
        <DrawTools drawMode={drawMode} onDrawModeChange={onDrawModeChange} />
      )}
      <div className='ns-map-zoom'>
        <MapButton title={t('new_season_home')} onClick={fitAll}>
          ⌂
        </MapButton>
        <MapButton title={t('new_season_zoom_in')} onClick={() => zoom(1)}>
          +
        </MapButton>
        <MapButton title={t('new_season_zoom_out')} onClick={() => zoom(-1)}>
          −
        </MapButton>
      </div>
      <MapSearch onNavigate={navigateTo} />
      <div className='ns-map-layers'>
        <MapButton
          title={t('new_season_layers')}
          onClick={() =>
            setBaseLayer((current) =>
              current === 'satellite' ? 'streets' : 'satellite',
            )
          }
        >
          {baseLayer === 'satellite'
            ? t('new_season_satellite')
            : t('new_season_streets')}
        </MapButton>
      </div>
    </div>
  );
};

module.exports = BlocksMap;
